import { Injectable } from '@angular/core';
import { MAT_NATIVE_DATE_FORMATS, MatDateFormats, NativeDateAdapter } from '@angular/material/core';

/**
 * Times show as `h:mm A` text ("3:30 PM"), as the forms and the API expect. The native adapter
 * formats with Intl, which in Chrome puts a narrow no-break space before AM/PM.
 */
const SB_TIME_FORMAT = 'h:mm A';

export const formatTimeText = (date: Date): string => {
  const hours = date.getHours();
  const minutes = String(date.getMinutes()).padStart(2, '0');
  return `${hours % 12 || 12}:${minutes} ${hours < 12 ? 'AM' : 'PM'}`;
};

/**
 * Date fields show `MM/DD/YYYY` ("10/06/2026"), zero-padded, as the ngx-bootstrap datepicker did;
 * Intl's numeric date drops the zeros. Forms that load a saved date keep it as that text.
 */
const SB_DATE_FORMAT = 'MM/DD/YYYY';
const DATE_TEXT_RE = /^(\d{1,2})\/(\d{1,2})\/(\d{4})$/;

export const formatDateText = (date: Date): string => {
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${month}/${day}/${date.getFullYear()}`;
};

@Injectable()
export class SbTimeDateAdapter extends NativeDateAdapter {
  override format(date: Date, displayFormat: object | string): string {
    if (displayFormat === SB_TIME_FORMAT) {
      return formatTimeText(date);
    }
    return displayFormat === SB_DATE_FORMAT ? formatDateText(date) : super.format(date, displayFormat as object);
  }

  /** Also takes a form's `MM/DD/YYYY` text, as midnight that day; the native adapter takes only ISO 8601. */
  override deserialize(value: unknown): Date | null {
    const match = typeof value === 'string' ? value.trim().match(DATE_TEXT_RE) : null;
    if (!match) {
      return super.deserialize(value);
    }
    const [month, day, year] = [Number(match[1]) - 1, Number(match[2]), Number(match[3])];
    const date = new Date(year, month, day);
    return date.getMonth() === month && date.getDate() === day ? date : this.invalid();
  }
}

export const SB_DATE_FORMATS: MatDateFormats = {
  parse: { ...MAT_NATIVE_DATE_FORMATS.parse, dateInput: SB_DATE_FORMAT },
  display: {
    ...MAT_NATIVE_DATE_FORMATS.display,
    dateInput: SB_DATE_FORMAT,
    timeInput: SB_TIME_FORMAT,
    timeOptionLabel: SB_TIME_FORMAT,
  },
};
